import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, DOCUMENT, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BreakpointObserver, LayoutModule } from '@angular/cdk/layout';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSidenav, MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { UserNotesDialogComponent } from './shared/components/user-notes-dialog/user-notes-dialog.component';

import { environment } from '../environments/environment';
import { AuthService } from './core/auth.service';
import { buildAdminMenu, INavGroup, INavItem } from './core/admin-menu';
import { IUser } from './user/user.interface';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
      CommonModule,
      RouterModule,
      LayoutModule,
      MatSidenavModule,
      MatToolbarModule,
      MatIconModule,
      MatButtonModule,
      MatListModule,
      MatMenuModule,
      MatSnackBarModule,
      MatDialogModule,
      MatTooltipModule,
      DragDropModule,
      RouterModule
    ],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  public theme: 'light' | 'dark' = 'light';
  public env = environment.env;
  public eng = environment.eng;
  public user$: Observable<IUser | null> = this.authService.user$;
  public currentUser: IUser | null = null;
  public admin = false;
  public isBigScreen = true;
  public menu = buildAdminMenu(this.eng);
  public query = '';
  public pageTitle = '';
  private readonly destroy$ = new Subject<void>();
  private readonly pinKey = 'admin-nav-pinned';
  private readonly collapsedKey = 'admin-nav-collapsed';
  public pinned: string[] = this.readStored(this.pinKey);
  public collapsed: string[] = this.readStored(this.collapsedKey);
  private readonly railKey = 'admin-nav-rail';
  public rail = this.readStored(this.railKey).length > 0;
  @ViewChild('navFilter') private navFilter?: ElementRef<HTMLInputElement>;
  @ViewChild(MatSidenavContainer) private sidenavContainer?: MatSidenavContainer;

  constructor(
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initTheme();
    this.observeScreenSize();
    this.authService.token$.pipe(
      filter(Boolean),
      takeUntil(this.destroy$)
    ).subscribe(() => this.authService.me().subscribe());

    this.authService.user$.pipe(
      takeUntil(this.destroy$),
    ).subscribe((user: IUser | null) => {
      this.currentUser = user;
      this.admin = Boolean(user?.role && user.role > 10);
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(event => this.markCurrentRoute(event.urlAfterRedirects));
    this.markCurrentRoute(this.router.url);
  }

  private initTheme(): void {
    // Three-step pick: explicit user choice > OS preference > light default.
    const storedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (storedTheme === 'light' || storedTheme === 'dark') {
      this.theme = storedTheme;
    } else if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      this.theme = 'dark';
    } else {
      this.theme = 'light';
    }
    this.document.body.classList.toggle('dark-theme', this.theme === 'dark');

    // If the user has NOT made an explicit choice yet, follow the OS as
    // they switch it (e.g. macOS auto night-shift). Once they click the
    // toggle, we stop listening and respect their pick exclusively.
    if (storedTheme !== 'light' && storedTheme !== 'dark' &&
        typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => {
        if (localStorage.getItem('theme')) return; // user took control
        this.theme = e.matches ? 'dark' : 'light';
        this.document.body.classList.toggle('dark-theme', this.theme === 'dark');
      };
      mql.addEventListener?.('change', handler);
    }
  }

  private observeScreenSize(): void {
    this.breakpointObserver.observe(['(min-width: 992px)'])
      .pipe(
        map(result => result.matches),
        takeUntil(this.destroy$)
      )
      .subscribe(matches => this.isBigScreen = matches);
  }

  get sidenavMode(): 'side' | 'over' {
    return this.isBigScreen ? 'side' : 'over';
  }

  // Icon-only rail; phones keep the full overlay menu.
  get isRail(): boolean {
    return this.rail && this.isBigScreen;
  }

  public toggleRail(): void {
    this.rail = !this.rail;
    this.writeStored(this.railKey, this.rail ? ['1'] : []);
    // The container measures the drawer only on open/close, so re-measure after the width class applies.
    setTimeout(() => this.sidenavContainer?.updateContentMargins());
  }

  // Every item in menu order, including the footer, used by Pinned.
  private get allItems(): INavItem[] {
    return [...this.menu.groups.flatMap(group => group.items), ...this.menu.footer];
  }

  public get filtering(): boolean {
    return this.query.trim() !== '';
  }

  private matchesQuery(item: INavItem): boolean {
    return item.visible && item.name.toLowerCase().includes(this.query.trim().toLowerCase());
  }

  // Pinned items in the order the user dragged them into.
  public get pinnedItems(): INavItem[] {
    const byLink = new Map(this.allItems.map(item => [item.link, item]));
    return this.pinned
      .map(link => byLink.get(link))
      .filter((item): item is INavItem => item !== undefined && this.matchesQuery(item));
  }

  public get groupsView(): INavGroup[] {
    return this.menu.groups
      .map(group => ({ ...group, items: group.items.filter(item => this.matchesQuery(item)) }))
      .filter(group => group.items.length > 0);
  }

  public get footerItems(): INavItem[] {
    return this.menu.footer.filter(item => this.matchesQuery(item));
  }

  public isGroupOpen(title: string): boolean {
    // The rail hides group titles, so it shows every item.
    return this.filtering || this.isRail || !this.collapsed.includes(title);
  }

  public toggleGroup(title: string): void {
    this.collapsed = this.collapsed.includes(title)
      ? this.collapsed.filter(t => t !== title)
      : [...this.collapsed, title];
    this.writeStored(this.collapsedKey, this.collapsed);
  }

  public isPinned(link: string): boolean {
    return this.pinned.includes(link);
  }

  public togglePin(link: string): void {
    this.pinned = this.isPinned(link)
      ? this.pinned.filter(l => l !== link)
      : [...this.pinned, link];
    this.writeStored(this.pinKey, this.pinned);
  }

  public dropPinned(event: CdkDragDrop<INavItem[]>): void {
    const links = this.pinnedItems.map(item => item.link);
    moveItemInArray(links, event.previousIndex, event.currentIndex);
    // Pins hidden by the role check are not in the list, so they keep their place at the end.
    this.pinned = [...links, ...this.pinned.filter(link => !links.includes(link))];
    this.writeStored(this.pinKey, this.pinned);
  }

  // Phones use an overlay sidenav, so close it once a page is picked.
  public closeOnSmall(sidenav: MatSidenav): void {
    if (!this.isBigScreen) {
      sidenav.close();
    }
  }

  // Keyboard shortcut: "/" jumps to the filter unless the user is already typing.
  @HostListener('document:keydown', ['$event'])
  public onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || !this.admin) return;
    const target = event.target as HTMLElement;
    if (target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
    event.preventDefault();
    this.navFilter?.nativeElement.focus();
  }

  private markCurrentRoute(url: string): void {
    const path = url.split(/[?#]/)[0] ?? '';
    const matches = (item: INavItem) => path === item.link || path.startsWith(item.link + '/');
    const item = this.allItems.find(matches);
    if (!item) {
      this.pageTitle = '';
      return;
    }
    const group = this.menu.groups.find(g => g.items.some(matches));
    this.pageTitle = group ? `${group.title} / ${item.name}` : item.name;
    // Open the group the user just navigated into.
    if (group && this.collapsed.includes(group.title)) {
      this.toggleGroup(group.title);
    }
  }

  private readStored(key: string): string[] {
    try {
      const stored = JSON.parse(localStorage.getItem(key) ?? '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  }

  private writeStored(key: string, value: string[]): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage can be blocked; the state still lasts for this session.
    }
  }

  public logOut(): void {
    if (confirm('Do you want to log out?')) {
      this.authService.logOut();
    }
  }

  public toggleTheme(): void {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    // Use boolean second arg so toggling stays in sync even if some other
    // code added/removed the class out-of-band.
    this.document.body.classList.toggle('dark-theme', this.theme === 'dark');
    localStorage.setItem('theme', this.theme);
  }

  public openUserNotes(): void {
    if (!this.currentUser?.id) return;

    const dialogWidth = this.isBigScreen ? '80%' : '95%';
    const dialogHeight = this.isBigScreen ? '80%' : '90%';
    const dialogMaxWidth = '1400px';
    const dialogMaxHeight = '800px';

    this.dialog.open(UserNotesDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      maxWidth: dialogMaxWidth,
      maxHeight: dialogMaxHeight,
      data: { userId: this.currentUser.id },
      panelClass: 'notes-dialog-container'
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
